import React, { useState, useRef, useEffect } from "react";
import { Row } from "react-bootstrap";
import html2canvas from "html2canvas";
import { MyDiv } from "../Common/Components";
import BarcodeSection from "./barcodeSection";
import axios from "axios";
import swal from "sweetalert2";
import PropTypes from "prop-types";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AWF_CUST_ID = localStorage.getItem("awfCustId");

// unitDbId (optional): when set, the component runs in per-unit mode — it
// fetches and posts the barcode against /api/order-units/:unitId instead of
// the order-level endpoints. Absent → unchanged order-level behavior.
// forceRender (optional): bypasses the order_barcode_checker gate so a
// resend on an already-pushed order/unit still re-renders + re-emails.
// Initial production push leaves this false so the gate still prevents
// accidental double-pushes.
function BarCodeList({ orderId, totalPacks, BarcodeSuccessInfo, unitDbId = null, forceRender = false }) {
  const [barCodeList, setBarcodeList] = useState([]);
  const wrapperRefs = {
    flashing: useRef(),
    jobbing: useRef(),
    faciagutter: useRef(),
    cladding: useRef(),
    gbi: useRef(),
    roofing: useRef(),
    rv: useRef(),
    awf: useRef(),
  };

  let savedOrderId = orderId;
  useEffect(() => {
    if (unitDbId || (savedOrderId?.id && savedOrderId?.department)) {
      loadSpecificBarcodeList();
    }
  }, [orderId?.updatedAt, unitDbId]);

  const loadSpecificBarcodeList = async () => {
    const url = unitDbId
      ? `${API_BASE_URL}api/order-units/${unitDbId}/barcode`
      : API_BASE_URL + `fetch-specific-order-barcode/${orderId.id}`;
    try {
      const response = await fetch(url, {
        headers: {
          "x-access-token": localStorage.getItem("token"),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data[0] && (forceRender || data[0].order_barcode_checker === false)) {
        setBarcodeList(data);
      }
    } catch (error) {
      BarcodeSuccessInfo();
      swal.fire({
        text: error.response?.data || "An error occurred",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    if (Array.isArray(barCodeList) && barCodeList.length !== 0) {
      if (orderId || unitDbId) {
        handleSaveAndSendToAPI();
      }
    }
  }, [barCodeList]);

  const [customData] = useState({
    pack_value: "",
    pn_value: "",
    q_value: ""
  })

  const [data] = useState({
    mode: 2,
    department: "J",
    packs: 1,
    pack_name: 1,
    quantity: 1,
  })

  function splitQuantityEvenly(originalQuantity, packs, mode, manualQuantitiesRaw = '') {
    if (mode === 3) {
      const manualQuantities = manualQuantitiesRaw
        .split(',')
        .map(q => parseInt(q.trim(), 10))
        .filter(q => !isNaN(q));
      return manualQuantities;
    }

    if (mode === 1) {
      return new Array(packs).fill(originalQuantity);
    }

    // Default: Split Equally
    const baseQty = Math.floor(originalQuantity / packs);
    const result = new Array(packs).fill(baseQty);
    const remaining = originalQuantity - (baseQty * packs);

    for (let i = 0; i < remaining; i++) {
      result[i] += 1;
    }

    return result;
  }

  const handleSaveAndSendToAPI = async () => {
    const canvasWidth = 1800;
    const canvasHeight = 1200;
    const opt = {
      scale: 4,
    };

    const formData = new FormData();
    let barcodeRefs;

    // Per-unit mode is always Flashing; order mode reads orderId.department.
    const department = unitDbId ? "F" : orderId.department;

    if (department === "F") {
      barcodeRefs = [wrapperRefs.flashing.current];
    } else if (department === "J") {
      barcodeRefs = [wrapperRefs.jobbing.current];
    } else if (department === "FG") {
      barcodeRefs = [wrapperRefs.faciagutter.current];
    } else if (department === "CL") {
      barcodeRefs = [wrapperRefs.cladding.current];
    } else if (department === "GBI") {
      barcodeRefs = [wrapperRefs.gbi.current];
    } else if (department === "ROOF") {
      barcodeRefs = [wrapperRefs.roofing.current];
    } else if (department === "RV") {
      barcodeRefs = [wrapperRefs.rv.current];
    } else if (orderId.department === "AWF") {
      barcodeRefs = [wrapperRefs.awf.current];
    }

    if (department !== "J") {
      for (let index = 0; index < barcodeRefs.length; index++) {
        const barcodeRef = barcodeRefs[index];

        try {
          const canvas = await html2canvas(barcodeRef, opt);
          const resizedCanvas = document.createElement("canvas");
          resizedCanvas.width = canvasWidth;
          resizedCanvas.height = canvasHeight;
          const ctx = resizedCanvas.getContext("2d");
          ctx.drawImage(canvas, 0, 0, canvasWidth, canvasHeight);
          const blob = await new Promise(resolve => resizedCanvas.toBlob(resolve, "image/jpeg", 0.9));
          formData.append("documents", blob, `barcode_${index}.jpg`);
        } catch (error) {
          console.log(error);
        }
      }
    } else {
      const totalGroups = parseInt(customData.pack_value || (data.mode === 2 && totalPacks)) || 1;
      const customLabels = customData.pn_value
        ? customData.pn_value.split(',').map(s => s.trim().toUpperCase())
        : [];
      const
        quantities = splitQuantityEvenly(totalPacks, totalGroups, data.quantity, customData.q_value)

      // Build cumulative pieces array to map groupIndex -> item index
      const jobbingItems = barCodeList[0]?.jobbing_items || [];
      const cumulativePieces = [];
      let cumSum = 0;
      for (const ji of jobbingItems) {
        cumSum += parseInt(ji.pieces) || 0;
        cumulativePieces.push(cumSum);
      }

      // Helper: find which item a given groupIndex belongs to
      const getItemIndexForGroup = (groupIdx) => {
        for (let i = 0; i < cumulativePieces.length; i++) {
          if (groupIdx < cumulativePieces[i]) return i;
        }
        return cumulativePieces.length - 1;
      };

      // Create separate arrays for DOM nodes - DO NOT push to quantities!
      const labelNodes = [];
      const qtyNodes = [];
      for (let groupIndex = 0; groupIndex < totalGroups; groupIndex++) {
        const labelPrefix = `${groupIndex + 1}`;
        const packName = customLabels.length > 0 ? ` (${customLabels[groupIndex]})` : ""
        const label = `Pack - ${labelPrefix}/${totalGroups}${packName}`;
        const qtyLabel = quantities[groupIndex];

        // Determine which item this sticker belongs to
        const currentItemIndex = getItemIndexForGroup(groupIndex);

        for (let index = 0; index < barcodeRefs.length; index++) {
          const barcodeRef = barcodeRefs[index];

          // Show only the matching item description, hide others
          const itemDescElements = barcodeRef.querySelectorAll('.jobbing-item-desc');
          const originalDisplays = [];
          for (let i = 0; i < itemDescElements.length; i++) {
            originalDisplays.push(itemDescElements[i].style.display);
            itemDescElements[i].style.display = i === currentItemIndex ? '' : 'none';
          }

          // Show only the matching piece circle, hide others
          const pieceCircleElements = barcodeRef.querySelectorAll('.jobbing-piece-circle');
          const originalCircleDisplays = [];
          for (let i = 0; i < pieceCircleElements.length; i++) {
            originalCircleDisplays.push(pieceCircleElements[i].style.display);
            pieceCircleElements[i].style.display = i === currentItemIndex ? 'inline-flex' : 'none';
          }

          // Find the original Pieces value element and replace it
          let originalPiecesElement = null;
          let originalPiecesValue = null;

          // Search for the element containing the total quantity (e.g., ":101" or "101")
          const allElements = barcodeRef.querySelectorAll('span, div, p, td');
          for (const el of allElements) {
            const text = el.innerText?.trim();
            // Match patterns like ":101", "101", ": 101"
            if (text === String(totalPacks) ||
              text === `:${totalPacks}` ||
              text === `: ${totalPacks}` ||
              text.includes(`:${totalPacks}`)) {
              originalPiecesElement = el;
              originalPiecesValue = el.innerText;
              // Replace with pack quantity
              if (totalGroups > 1) {
                el.innerText = text.includes(':') ? `:${qtyLabel}` : String(qtyLabel);
              }
              break;
            }
          }

          const labelNode = document.createElement('div');
          labelNode.innerText = label;
          labelNode.style.position = 'absolute';
          // labelNode.style.top = '150px';
          labelNode.style.top = barCodeList[0]?.account_UID === AWF_CUST_ID ? '270px' : '150px';
          labelNode.style.left = '40px';
          labelNode.style.color = '#d22530';
          labelNode.style.background = 'transparent';
          labelNode.style.fontSize = '100px';
          labelNode.style.fontWeight = 'bold';
          labelNode.style.zIndex = '9999';

          barcodeRef.style.position = 'relative';

          if (totalGroups > 1) {
            labelNodes.push(labelNode);
            barcodeRef.appendChild(labelNode);
          }

          await new Promise(resolve => setTimeout(resolve, 100));

          try {
            const canvas = await html2canvas(barcodeRef, opt);
            const resizedCanvas = document.createElement('canvas');
            resizedCanvas.width = canvasWidth;
            resizedCanvas.height = canvasHeight;
            const ctx = resizedCanvas.getContext('2d');
            ctx.drawImage(canvas, 0, 0, canvasWidth, canvasHeight);

            const blob = await new Promise((resolve) =>
              resizedCanvas.toBlob(resolve, 'image/jpeg', 0.9)
            );

            formData.append('documents', blob, `group_${groupIndex}_barcode_${index + 1}.jpg`);
          } catch (error) {
            console.error('Error capturing barcode:', error);
          } finally {
            // Restore original Pieces value after capture
            if (originalPiecesElement && originalPiecesValue !== null) {
              originalPiecesElement.innerText = originalPiecesValue;
            }
            if (totalGroups > 1) {
              barcodeRef.removeChild(labelNode);
            }
            // Restore item description visibility
            for (let i = 0; i < itemDescElements.length; i++) {
              itemDescElements[i].style.display = originalDisplays[i];
            }
            // Restore piece circle visibility
            for (let i = 0; i < pieceCircleElements.length; i++) {
              pieceCircleElements[i].style.display = originalCircleDisplays[i];
            }
          }
        }
      }
    }

    // Add AWF notes if present
    if (orderId?.department === "AWF" && orderId.awfNotes) {
      formData.append("awfNotes", orderId.awfNotes);
    }

    const apiEndpoint = unitDbId
      ? `${API_BASE_URL}api/order-units/${unitDbId}/sent-barcode-designs`
      : `${API_BASE_URL}sent-barcode-designs-for-printing/${orderId.id}/${orderId.department}`;
    try {
      await axios.post(apiEndpoint, formData, {
        headers: {
          "x-access-token": localStorage.getItem("token"),
          Accept: "application/json",
        },
      });
      swal.fire({
        text: "Processed Successfully",
        icon: "success",
        type: "success",
        timer: 1000,
      });
      BarcodeSuccessInfo();
      savedOrderId = "";
    } catch (error) {
      BarcodeSuccessInfo();
      swal.fire({
        text: error.response.data,
        icon: "error",
        type: "error",
      });
    }
  };

  return (
    <React.Fragment>
      {barCodeList.map(item => (
        <MyDiv key={item._id}>
          <Row>
            {(unitDbId || item.Order_Flashing_Count !== 0) && <BarcodeSection item={item} department="Flashing" barcodeRef={wrapperRefs.flashing} />}
            {item.Order_Jobbing_Count !== 0 && <BarcodeSection item={item} department="Jobbing" barcodeRef={wrapperRefs.jobbing} />}
            {item.Order_Faciagutter_Count !== 0 && <BarcodeSection item={item} department="Facia Gutter" barcodeRef={wrapperRefs.faciagutter} />}
            {item.Order_Cladding_Count !== 0 && <BarcodeSection item={item} department="Cladding" barcodeRef={wrapperRefs.cladding} />}
            {item.Order_GBI_Count !== 0 && <BarcodeSection item={item} department="GBI" barcodeRef={wrapperRefs.gbi} />}
            {item.Order_Roofing_Count !== 0 && <BarcodeSection item={item} department="Roofing" barcodeRef={wrapperRefs.roofing} />}
            {item.Order_RV_Count !== 0 && <BarcodeSection item={item} department="RV" barcodeRef={wrapperRefs.rv} />}
          </Row>
        </MyDiv>
      ))}
    </React.Fragment>
  );
}

export default BarCodeList;

BarCodeList.propTypes = {
  orderId: PropTypes.any,
  BarcodeSuccessInfo: PropTypes.any,
};